import { Component, inject, OnInit, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { DynamicForm, SelectOption } from '../../../../components/form-builder/form-builder.types';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { UserService } from '../user.service';
import { getUserFormObject } from '../utils/user-form-object';
import { UserRepository } from '@/store/user-state/user-state.repository';
import { MessageService } from 'primeng/api';
import { getFormBuilderObjectFactory } from '../../../../components/form-builder/utils/form-builder-factory';
import { Router } from '@angular/router';
import { SpinnerComponent } from '../../../../components/shared/spinner/spinner.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContractingAuthorityService } from '../../contracting-authority/contracting-authority.service';
import { CommitteeService } from '../../committee/committee.service';
import { forkJoin } from 'rxjs';
import { Subscription } from 'rxjs';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { FieldVariant } from '../../../../components/form-builder/form-builder.types';
import { UserStringDto } from '@/types';

@Component({
  selector: 'sm-add-user',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    ButtonModule,
    TabsModule,
    CommonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    DialogModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerModule,
    TabsModule,
    SpinnerComponent,
    TranslateModule,
  ],
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss',
})
export class AddUserComponent implements OnInit, OnDestroy {
  userService = inject(UserService);
  userRepository = inject(UserRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);
  contractingAuthorityService = inject(ContractingAuthorityService);
  committeeService = inject(CommitteeService);
  cdr = inject(ChangeDetectorRef);

  @ViewChild(FormBuilderComponent) formBuilderComponent!: FormBuilderComponent;
  @ViewChild('dialogComponent') dialogComponent!: DialogComponent;

  displaySubMenu = false;

  breadcrumb = [
    {
      label: 'settings.user.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.user.breadcrumb.user',
      routerLink: ['/settings', 'user'],
    },
    {
      label: 'settings.user.breadcrumb.new',
      routerLink: ['/settings', 'user', 'add'],
    },
  ];

  formObject: DynamicForm = getFormBuilderObjectFactory();
  formDateLoading = false;
  loading = false;

  rolesOptions: SelectOption[] = [];
  acsOptions: SelectOption[] = [];
  comsOptions: SelectOption[] = [];

  private acsSub?: Subscription;

  ngOnInit(): void {
    (window as any).ngRef = this;
    this.formDateLoading = true;
    this.userRepository.reset();

    // Initialize empty arrays first
    this.rolesOptions = [];
    this.acsOptions = [];
    this.comsOptions = [];

    // Create initial form with empty options
    this.formObject = getUserFormObject({
      store: this.userRepository,
      rolesOptions: this.rolesOptions,
      acsOptions: this.acsOptions,
      comsOptions: this.comsOptions,
    });

    forkJoin({
      roles: this.userService.getAllRoles(),
      acs: this.contractingAuthorityService.getContractingAuthoritiesListUm(),
      coms: this.committeeService.getCommitteesList(),
    }).subscribe({
      next: ({ roles, acs, coms }) => {
        this.rolesOptions = roles.map((role: any) => {
          if (typeof role === 'string') {
            return { name: role, code: role, label: role };
          }
          return {
            name: role.label || role.name, // Display label in dropdown
            code: role.name, // Use name for submission
            label: role.label || role.name, // Keep label for reference
          };
        });
        this.acsOptions = acs.map((ac: any) => ({ name: ac.nom, code: ac.id }));
        this.comsOptions = coms.map((com: any) => ({ name: com.nom, code: com.id }));

        // Update form with new options
        this.formObject = getUserFormObject({
          store: this.userRepository,
          rolesOptions: this.rolesOptions,
          acsOptions: this.acsOptions,
          comsOptions: this.comsOptions,
        });
        this.formDateLoading = false;
      },
      error: () => {
        this.formDateLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.loading'),
        });
      },
    });
    // Subscribe to selectedAcs changes and dynamically update acPiloteField options
    this.acsSub = this.userRepository.getObservable<SelectOption[] | undefined>('selectedAcs').subscribe(selectedAcs => {
      // Find the acPiloteField in the formObject and update its options
      // Assuming acPiloteField is in the last grid, last gridItem, first field
      const acPiloteField = this.formObject.grids[0]?.gridItems[6]?.fields[0]; // Adjust index if needed

      if (acPiloteField && acPiloteField.variant === FieldVariant.SELECT && acPiloteField.props) {
        const newAcPiloteOptions = (selectedAcs || []).map((ac: SelectOption) => ({ name: ac.name, code: ac.code }));
        acPiloteField.props['options'] = newAcPiloteOptions;

        // Check if the currently selected acPiloteId is still valid
        const currentAcPiloteId = this.userRepository.getNested('dto', 'acPiloteId') as number | undefined;
        if (currentAcPiloteId && !newAcPiloteOptions.some((opt: SelectOption) => opt.code === currentAcPiloteId)) {
          // If current acPiloteId is no longer in options, clear it
          this.userRepository.setNested('dto', 'acPiloteId', undefined);
          this.userRepository.set('acPiloteSelected', undefined);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.acsSub?.unsubscribe();
  }

  // Utility to check if the form is valid (all required fields filled and valid)
  get isFormValid(): boolean {
    const dto = this.userRepository.get('dto') as any;
    // Required fields
    const requiredFields = ['username', 'firstName', 'lastName', 'email', 'roles']; // Removed 'acIds', 'acPiloteId', 'comIds'

    for (const field of requiredFields) {
      const fieldValue = dto[field];
      const isArrayAndEmpty = Array.isArray(fieldValue) && fieldValue.length === 0;
      const isFalsy = !fieldValue;

      if (isFalsy || isArrayAndEmpty) {
        return false;
      }
    }
    // Username must not contain spaces
    if (typeof dto.username === 'string' && dto.username.includes(' ')) {
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(dto.email)) {
      return false;
    }

    // Password match
    return true;
  }

  addUser() {
    // Set all touched flags to true
    this.userRepository.set('usernameTouched', true);
    this.userRepository.set('firstNameTouched', true);
    this.userRepository.set('lastNameTouched', true);
    this.userRepository.set('emailTouched', true);

    // Validate all fields
    const dto = this.userRepository.get('dto') as any;
    const usernameHasSpace = typeof dto.username === 'string' && dto.username.includes(' ');
    this.userRepository.set('usernameError', !dto.username || usernameHasSpace);
    this.userRepository.set('firstNameError', !dto.firstName);
    this.userRepository.set('lastNameError', !dto.lastName);
    this.userRepository.set('emailError', !dto.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dto.email));

    // Recreate the form object to ensure error observables are fresh
    this.formObject = getUserFormObject({
      store: this.userRepository,
      rolesOptions: this.rolesOptions,
      acsOptions: this.acsOptions,
      comsOptions: this.comsOptions,
    });

    if (!this.isFormValid) {
      this.userRepository.set('formSubmitted', true);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulaire invalide',
        detail: 'Veuillez remplir tous les champs obligatoires avec des données valides.',
      });
      return;
    }
    this.userRepository.set('formSubmitted', false);
    // Directly add user and redirect on success
    this.loading = true;
    const requestBody = {
      username: dto.username,
      firstName: dto.firstName,
      lastName: dto.lastName,
      email: dto.email,
      address: dto.address,
      mobile: dto.mobile,
      fixe: dto.fixe,
      comIds: Array.isArray(dto.comIds) ? dto.comIds.map((c: any) => (typeof c === 'number' ? c : c.code)) : [],
      acIds: Array.isArray(dto.acIds) ? dto.acIds.map((a: any) => (typeof a === 'number' ? a : a.code)) : [],
      acPiloteId: typeof dto.acPiloteId === 'number' ? dto.acPiloteId : Number(dto.acPiloteId),
      roles: Array.isArray(dto.roles) ? dto.roles.map((r: any) => (typeof r === 'string' ? r : r.name)) : [],
      enable: true,
    };
    this.userService.addUser(requestBody).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.userRepository.reset();
        this.formObject = getUserFormObject({
          store: this.userRepository,
          rolesOptions: this.rolesOptions,
          acsOptions: this.acsOptions,
          comsOptions: this.comsOptions,
        });
        // Show notification and redirect
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('settings.user.add.successTitle'),
          detail: response?.message || this.translate.instant('settings.user.add.successDescription'),
        });
        this.router.navigate(['/settings', 'user']);
      },
      error: error => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('settings.user.add.errorTitle'),
          detail: error?.error?.message || this.translate.instant('settings.user.add.errorDescription'),
        });
      },
    });
  }

  resetForm() {
    this.userRepository.reset();
    this.userRepository.set('formSubmitted', false);
    this.formObject = getUserFormObject({
      store: this.userRepository,
      rolesOptions: this.rolesOptions,
      acsOptions: this.acsOptions,
      comsOptions: this.comsOptions,
    });
  }

  onSubmit() {
    const store = this.userRepository;
    const dto = store.get('dto') as UserStringDto;

    // Validate all required fields
    const errors = {
      username: !dto?.username || (typeof dto?.username === 'string' && dto.username.includes(' ')),
      firstName: !dto?.firstName,
      lastName: !dto?.lastName,
      email: !dto?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dto.email),
    };

    // Set error states
    store.set('usernameError', errors.username);
    store.set('firstNameError', errors.firstName);
    store.set('lastNameError', errors.lastName);
    store.set('emailError', errors.email);

    // Check if there are any errors
    const hasErrors = Object.values(errors).some(error => error);
    if (hasErrors) {
      if (errors.username) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Formulaire invalide',
          detail: "Le nom d'utilisateur ne doit pas contenir d'espaces.",
        });
      }
      return;
    }

    // Proceed with form submission
    this.userService.addUser(dto).subscribe({
      next: response => {
        store.set('response', response);
        this.router.navigate(['/settings/user']);
      },
      error: (error: Error) => {
        console.error('Error creating user:', error);
      },
    });
  }

  onFieldBlur() {
    this.formObject = getUserFormObject({
      store: this.userRepository,
      rolesOptions: this.rolesOptions,
      acsOptions: this.acsOptions,
      comsOptions: this.comsOptions,
    });
  }
}
